import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Btn, Field, Input, Select, Textarea, Modal, DataTable, Plus, toast } from "@/pages/app/kit";
import { StatusBadge } from "@/components/shared";

function useList(url) {
  const [rows, setRows] = useState([]);
  const load = () => api.get(url).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return [rows, load];
}

export function LogisticsPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [rows, load] = useList("/transport");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ origin: "", destination: "", product: "", quantity: 0, pickup_window: "", refrigeration: false });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => { e.preventDefault(); try { await api.post("/transport", { ...f, quantity: parseFloat(f.quantity)||0 }); toast.success("OK"); setOpen(false); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const setStatus = async (id, status) => { await api.patch(`/transport/${id}/status`, { status }); load(); };
  const STATUSES = ["Requested","Quoted","Assigned","Pickup scheduled","Collected","In transit","Delivered","Exception","Cancelled"];
  const cols = [
    { key: "product", label: "Product" }, { key: "quantity", label: "Qty", render: (r) => `${r.quantity} kg` },
    { key: "origin", label: "From" }, { key: "destination", label: "To" },
    { key: "refrigeration", label: "Cold", render: (r) => r.refrigeration ? "❄" : "—" },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", label: "", render: (r) => <Select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="text-xs">{STATUSES.map((s)=><option key={s}>{s}</option>)}</Select> },
  ];
  return (<div>
    <PageTitle title={t("dashboard.logistics")} action={<Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-transport-btn">{t("cta.create")}</Btn>} />
    <DataTable columns={cols} rows={rows} empty={t("empty")} testid="transport-table" />
    <Modal open={open} onClose={() => setOpen(false)} title={t("dashboard.logistics")}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Product"><Input value={f.product} onChange={set("product")} /></Field>
          <Field label="Quantity (kg)"><Input type="number" value={f.quantity} onChange={set("quantity")} /></Field>
          <Field label="Origin"><Input value={f.origin} onChange={set("origin")} /></Field>
          <Field label="Destination"><Input value={f.destination} onChange={set("destination")} /></Field>
          <Field label="Pickup window"><Input value={f.pickup_window} onChange={set("pickup_window")} /></Field>
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.refrigeration} onChange={set("refrigeration")} /> {L("Chłodzenie","Refrigeration")}</label>
        <Btn type="submit">{t("cta.create")}</Btn>
      </form>
    </Modal>
  </div>);
}

export function RecoveryPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const [rows, load] = useList("/recovery");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ quantity: 0, reason: "", destination: "Processing", processor: "", estimated_value: 0, food_loss_avoided: 0, impact_status: "Estimated" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => { e.preventDefault(); try { await api.post("/recovery", { ...f, quantity: parseFloat(f.quantity)||0, estimated_value: parseFloat(f.estimated_value)||0, food_loss_avoided: parseFloat(f.food_loss_avoided)||0 }); toast.success("OK"); setOpen(false); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const cols = [
    { key: "destination", label: "Destination" }, { key: "quantity", label: "Qty", render: (r) => `${r.quantity} kg` },
    { key: "reason", label: "Reason" }, { key: "estimated_value", label: "Value", render: (r) => `${r.estimated_value} PLN` },
    { key: "impact_status", label: "Impact", render: (r) => <span className="rounded bg-secondary px-2 py-0.5 text-xs">{r.impact_status}</span> },
  ];
  return (<div>
    <PageTitle title={t("dashboard.recovery")} subtitle={L("Odzysk nieuniknionych pozostałości","Recovery of unavoidable residues")} action={<Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-recovery-btn">{t("cta.create")}</Btn>} />
    <DataTable columns={cols} rows={rows} empty={t("empty")} testid="recovery-table" />
    <Modal open={open} onClose={() => setOpen(false)} title={t("dashboard.recovery")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Destination"><Select value={f.destination} onChange={set("destination")}>{["Fresh redistribution","Processing","Donation","Animal feed","Biogas","Compost","Fertilizer input","Controlled disposal"].map((x)=><option key={x}>{x}</option>)}</Select></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Quantity (kg)"><Input type="number" value={f.quantity} onChange={set("quantity")} /></Field>
          <Field label="Est. value (PLN)"><Input type="number" value={f.estimated_value} onChange={set("estimated_value")} /></Field>
          <Field label="Food loss avoided (kg)"><Input type="number" value={f.food_loss_avoided} onChange={set("food_loss_avoided")} /></Field>
          <Field label="Impact status"><Select value={f.impact_status} onChange={set("impact_status")}>{["Estimated","Partner reported","Verified"].map((x)=><option key={x}>{x}</option>)}</Select></Field>
        </div>
        <Field label="Reason"><Textarea rows={2} value={f.reason} onChange={set("reason")} /></Field>
        <Btn type="submit">{t("cta.create")}</Btn>
      </form>
    </Modal>
  </div>);
}

export function DisputesPage() {
  const { t, lang } = useT();
  const L = (pl, en) => (lang === "pl" ? pl : en);
  const { user } = useAuth();
  const canResolve = ["quality","coordinator","admin","super_admin"].includes(user.role);
  const [rows, load] = useList("/disputes");
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject: "grade", description: "" });
  const STATUSES = ["Open","Evidence requested","Under review","Independent review","Resolved","Escalated","Closed"];
  const submit = async (e) => { e.preventDefault(); try { await api.post("/disputes", f); toast.success("OK"); setOpen(false); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const setStatus = async (id, status) => { await api.patch(`/disputes/${id}/status`, { status }); load(); };
  const cols = [
    { key: "subject", label: "Subject" }, { key: "description", label: "Description", render: (r) => <span className="text-mutedtext">{r.description}</span> },
    { key: "raised_by_name", label: "Raised by" }, { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "act", label: "", render: (r) => canResolve ? <Select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} className="text-xs">{STATUSES.map((s)=><option key={s}>{s}</option>)}</Select> : null },
  ];
  return (<div>
    <PageTitle title={t("dashboard.disputes")} action={<Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-dispute-btn">{t("cta.create")}</Btn>} />
    <DataTable columns={cols} rows={rows} empty={t("empty")} testid="disputes-table" />
    <Modal open={open} onClose={() => setOpen(false)} title={t("dashboard.disputes")}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={L("Temat","Subject")}><Select value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })}>{["weight","grade","rejection","deduction","delivery acceptance","settlement"].map((x)=><option key={x}>{x}</option>)}</Select></Field>
        <Field label={L("Opis","Description")} required><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} required /></Field>
        <Btn type="submit">{t("cta.create")}</Btn>
      </form>
    </Modal>
  </div>);
}
